import { useMutation } from '@tanstack/react-query';
import { Badge, Button, KanbanTag, Select, cardClasses, cn, linkTextClasses } from '@evogard/ui';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { DESK_BAND_LABELS, format as formatMoney, formatDate, fromColumn } from '@evogard/domain';
import type { DeskSummary } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { canPull, destinationOf, type BoardCard } from './deskBoard.js';
import { cardDomId } from './deskFocus.js';
import { useDeskMove, type DeskMove } from './DeskMoveDialog.js';
import { useInvalidateDesk } from './useDesk.js';

/**
 * Um card do quadro — o `Card` do `KanbanCobranca` (`App.tsx:210-734`).
 *
 * O selo da base no canto (CE laranja, GO azul no legado; aqui o tom neutro com
 * a sigla), nome, telefone, placa, valor, vencimento, quem está com ele e as
 * etiquetas. "Puxar Atendimento" e "contato feito hoje" (5.519 cards marcados
 * em produção, 19/10/2026) ficam no próprio card, porque são os de um clique e
 * os mais usados no legado.
 *
 * "Mover para…" é o "Transferir para..." do legado (`App.tsx:604-640`) mais os
 * botões de pago (`:324-327`): passar a alguém, devolver à fila e dar como
 * pago, sem abrir a lateral. Todo movimento passa pelo `DeskMoveDialog`, que
 * diz para onde o card vai antes do sim.
 */
export function DeskCardView({
  card,
  summary,
  onOpen,
  highlight = false,
  selected = false,
  broughtHere = false,
  anchored = false,
}: {
  card: BoardCard;
  /** O resumo da base do card — o que a pessoa pode nela, e a lista de operadores. */
  summary: DeskSummary;
  onOpen: () => void;
  /** O card que "Mostrar no quadro" trouxe — fica com o anel por uns instantes. */
  highlight?: boolean;
  /**
   * O card aberto na lateral agora — fica marcado enquanto ela estiver aberta,
   * para quem olha o quadro saber de qual card é a lateral (pedido de 28/10/2026).
   */
  selected?: boolean;
  /** Ainda não estava entre os carregados da coluna, e veio para o topo dela. */
  broughtHere?: boolean;
  /**
   * O card do QUADRO, que "Mostrar no quadro" procura pelo `id`. O da busca
   * não leva: com os dois, a rolagem acharia primeiro o da busca, que já está
   * na tela, e não sairia do lugar.
   */
  anchored?: boolean;
}) {
  const invalidate = useInvalidateDesk();
  const start = useDeskMove();
  const { can, me } = summary;
  const mine = card.operator?.id === me;
  const open = card.closedAt === null;
  const wide = can.distribute;

  const contact = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingDesk.contactToday({
          params: { tenant: card.tenant.slug, id: card.id },
          body: { done: !card.contactedToday },
        }),
        200,
        'Não foi possível marcar o contato.',
      ),
    onSettled: () => invalidate(),
  });

  const amount = card.amount === null ? null : fromColumn(card.amount);
  const canContact = open && can.handle && (mine || wide);
  const pullable = canPull(card, summary, me);
  const moves = open ? movesOf(card, summary) : [];
  const act = (move: DeskMove) => start({ card, move, summary });

  return (
    /*
     * O clique em qualquer parte do card abre a lateral, como no Kanban — e não
     * só no nome (pedido do dono, 27/10/2026). O que é controle dentro do card
     * (botões, "Mover para…") continua fazendo o que faz. Pelo teclado, quem
     * abre é o botão do nome; o clique no corpo é atalho do mouse.
     */
    <article
      id={anchored ? cardDomId(card.tenant.slug, card.id) : undefined}
      aria-current={selected ? 'true' : undefined}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a, select, input, textarea, label, [role="combobox"]')) return;
        onOpen();
      }}
      className={cardClasses({
        density: 'densa',
        tone: card.contactedToday ? 'sucesso' : 'neutro',
        className: cn(
          'flex scroll-m-6 cursor-pointer flex-col gap-2.5',
          selected && 'ring-2 ring-primaria',
          highlight && 'ring-4 ring-primaria animate-in fade-in-0 zoom-in-95 duration-500',
        ),
      })}
    >
      {broughtHere && (
        <p className="text-xs font-semibold text-texto">
          Trazido até aqui — o card está nesta coluna.
        </p>
      )}
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 text-left text-base font-semibold text-texto hover:underline"
        >
          <span className="line-clamp-2 break-words">{card.name ?? 'Sem nome'}</span>
        </button>
        <Badge>{card.tenant.slug.toUpperCase()}</Badge>
      </div>

      {/*
        * `minmax(0,1fr)` e não a grade implícita: a trilha `auto` cresce até o
        * texto mais largo sem quebra, e o nome comprido de um consultor
        * empurrava a linha para fora do card (relato de 20/10/2026).
        */}
      <dl className="grid grid-cols-[minmax(0,1fr)] gap-1 text-sm text-texto-suave">
        {card.phone && <Line label="Telefone">{card.phone}</Line>}
        {card.plates.length > 0 && (
          <Line label="Placa">
            <ReadMore text={card.plates.join(', ')} />
          </Line>
        )}
        {amount !== null && (
          <Line label="Valor">
            <span className="font-semibold text-sucesso">{formatMoney(amount)}</span>
          </Line>
        )}
        {card.dueDate && <Line label="Vencimento">{formatDate(card.dueDate)}</Line>}
        {card.operator && (
          <Line label="Atendente">
            <ReadMore text={card.operator.name} />
          </Line>
        )}
        {card.consultant && (
          <Line label="Consultor">
            <ReadMore text={card.consultant} />
          </Line>
        )}
      </dl>

      {(card.labels.length > 0 || card.erpPaidOn || card.boleto?.canceled) && (
        <div className="flex flex-wrap gap-1">
          {card.erpPaidOn && (
            <KanbanTag tone="sucesso" icon="confirmado" title="O boleto consta pago na casa">
              Pago no ERP em {formatDate(card.erpPaidOn)}
            </KanbanTag>
          )}
          {card.boleto?.canceled && <KanbanTag tone="erro">Boleto cancelado</KanbanTag>}
          {card.labels.map((l) => (
            <KanbanTag key={l.id} color={l.color}>
              {l.name}
            </KanbanTag>
          ))}
        </div>
      )}

      {card.note && (
        <div className="rounded-borda bg-alerta-suave px-2 py-1 text-sm text-texto">
          <ReadMore text={card.note} lines={3} />
        </div>
      )}

      {(pullable || canContact) && (
        <div className="flex flex-wrap items-center gap-2">
          {pullable && (
            <Button size="sm" onClick={() => act({ kind: 'pull' })}>
              Puxar atendimento
            </Button>
          )}
          {canContact && (
            <Button
              size="sm"
              variant={card.contactedToday ? 'secundario' : 'contorno'}
              icon="telefone"
              loading={contact.isPending}
              onClick={() => contact.mutate()}
              aria-pressed={card.contactedToday}
            >
              {card.contactedToday ? 'Contato feito hoje' : 'Marcar contato de hoje'}
            </Button>
          )}
        </div>
      )}

      {moves.length > 0 && (
        <Select
          label={`Mover ${card.name ?? 'o card'}`}
          hiddenLabel
          value=""
          onChange={(e) => {
            const chosen = moves.find((m) => m.value === e.target.value);
            if (chosen) act(chosen.move);
          }}
        >
          <option value="">Mover para…</option>
          {groupsOf(moves).map(([group, items]) => (
            <optgroup key={group} label={group}>
              {items.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
      )}

      {contact.error && (
        <p role="alert" className="text-xs font-medium text-erro">
          {contact.error.message}
        </p>
      )}
    </article>
  );
}

interface MoveOption {
  value: string;
  label: string;
  group: string;
  move: DeskMove;
}

/**
 * O que "Mover para…" oferece neste card, para esta pessoa — a mesma regra do
 * servidor (`BillingDeskService.handOver` e `.pay`): passar para alguém e
 * devolver à fila é só de quem distribui; o operador dá como pago o card que é
 * dele (decisão do dono, 27/10/2026).
 *
 * O destino de "passar" já vem com a coluna em que o card cai — "Atraso 6-30
 * — Claudenir" —, porque a faixa sai do vencimento e não se escolhe.
 */
function movesOf(card: BoardCard, summary: DeskSummary): MoveOption[] {
  const { can, me } = summary;
  const wide = can.distribute;
  const mine = card.operator?.id === me;
  const out: MoveOption[] = [];

  if (can.assign && wide) {
    for (const o of summary.operators) {
      const sameFlow = card.flow === 'receptiva' ? o.receptiveFlow : o.activeFlow;
      if (!o.listed || !sameFlow || o.userId === card.operator?.id) continue;
      out.push({
        value: `passar:${o.userId}`,
        label: destinationOf(card, o),
        group: 'Passar para',
        move: { kind: 'pass', operator: o },
      });
    }
    if (wide && card.flow === 'ativa' && card.operator !== null) {
      out.push({
        value: 'fila',
        label: card.band ? `Devolver à fila mãe — ${DESK_BAND_LABELS[card.band]}` : 'Devolver à fila mãe',
        group: 'Fila mãe',
        move: { kind: 'queue' },
      });
    }
  }
  if (can.handle && (mine || wide)) {
    out.push(
      { value: 'pago_automatico', label: 'Pago automático', group: 'Dar como pago', move: { kind: 'autoPaid' } },
      { value: 'pago_cobranca', label: 'Pago cobrança (com comprovante)', group: 'Dar como pago', move: { kind: 'collectionPaid' } },
    );
  }
  return out;
}

function groupsOf(moves: MoveOption[]): Array<[string, MoveOption[]]> {
  const groups = new Map<string, MoveOption[]>();
  for (const m of moves) groups.set(m.group, [...(groups.get(m.group) ?? []), m]);
  return [...groups];
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 gap-1">
      <dt className="shrink-0">{label}:</dt>
      <dd className="min-w-0 flex-1 text-texto">{children}</dd>
    </div>
  );
}

/**
 * Texto que corta em `lines` linhas e abre no "ler mais". O botão só aparece
 * quando o texto de fato não coube — a medida é do navegador, e é refeita
 * quando a coluna muda de largura.
 */
function ReadMore({ text, lines = 1 }: { text: string; lines?: 1 | 3 }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [cut, setCut] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () => setCut(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <>
      <span
        ref={ref}
        className={cn(
          'block whitespace-pre-line break-words',
          !expanded && (lines === 1 ? 'line-clamp-1' : 'line-clamp-3'),
        )}
      >
        {text}
      </span>
      {(cut || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className={linkTextClasses('text-xs font-medium')}
        >
          {expanded ? 'ler menos' : 'ler mais'}
        </button>
      )}
    </>
  );
}
